import { useEffect, useState } from 'react';
import { Link, NavLink, Route, Routes, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { fetchGuideline, fetchGuidelines } from '@/api/guidelines';
import { MarkdownContent } from '@/components/guidelines/MarkdownContent';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { Spinner } from '@/components/ui/Spinner';
import { useI18n } from '@/i18n';
import type { GuidelineDetail, GuidelineSummary } from '@/types';

function GuidelineArticle() {
  const { slug } = useParams<{ slug: string }>();
  const { accessToken, sessionId } = useAuth();
  const { t, locale } = useI18n();
  const [guideline, setGuideline] = useState<GuidelineDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug || !accessToken) return;
    setLoading(true);
    fetchGuideline(slug, accessToken, sessionId)
      .then(setGuideline)
      .catch(() => setError(t('guidelines.notFound')))
      .finally(() => setLoading(false));
  }, [slug, accessToken, sessionId, locale, t]);

  if (loading) {
    return (
      <div className="page-loading">
        <Spinner label={t('guidelines.articleLoading')} />
      </div>
    );
  }

  if (error || !guideline) {
    return <ErrorMessage message={error ?? t('guidelines.notFoundShort')} />;
  }

  return (
    <article className="guideline-article glass-panel-soft">
      <header className="guideline-article__header">
        <p className="eyebrow">{guideline.type ?? t('guidelines.typeGuideline')}</p>
        <h1 className="guideline-article__title display">{guideline.title}</h1>
        {guideline.updatedAt ? (
          <p className="muted-text">{t('guidelines.updated', { date: guideline.updatedAt })}</p>
        ) : null}
      </header>
      {guideline.body ? (
        <MarkdownContent content={guideline.body} />
      ) : (
        <p className="markdown-content__p">
          {t('guidelines.external')}{' '}
          {guideline.bodyUrl ? (
            <a href={guideline.bodyUrl} target="_blank" rel="noopener noreferrer">
              {t('guidelines.openDocument')}
            </a>
          ) : null}
        </p>
      )}
    </article>
  );
}

function GuidelinesIndex() {
  const { accessToken, sessionId } = useAuth();
  const { t, locale } = useI18n();
  const [items, setItems] = useState<GuidelineSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('');

  useEffect(() => {
    if (!accessToken) return;
    setLoading(true);
    fetchGuidelines(accessToken, typeFilter || undefined, sessionId)
      .then(setItems)
      .catch(() => setError(t('guidelines.error')))
      .finally(() => setLoading(false));
  }, [accessToken, sessionId, typeFilter, locale, t]);

  return (
    <>
      <header className="page-header reveal is-in">
        <p className="eyebrow">{t('guidelines.eyebrow')}</p>
        <h1 className="page-title display">{t('guidelines.title')}</h1>
        <p className="page-lead">{t('guidelines.lead')}</p>
      </header>

      <div className="guidelines-filters reveal is-in reveal-delay-1">
        <button
          type="button"
          className={`category-chip${!typeFilter ? ' is-active' : ''}`}
          onClick={() => setTypeFilter('')}
        >
          {t('guidelines.all')}
        </button>
        <button
          type="button"
          className={`category-chip${typeFilter === 'integration' ? ' is-active' : ''}`}
          onClick={() => setTypeFilter('integration')}
        >
          {t('guidelines.integration')}
        </button>
        <button
          type="button"
          className={`category-chip${typeFilter === 'assets' ? ' is-active' : ''}`}
          onClick={() => setTypeFilter('assets')}
        >
          {t('guidelines.assets')}
        </button>
      </div>

      {loading ? (
        <div className="page-loading">
          <Spinner label={t('guidelines.loading')} />
        </div>
      ) : null}

      {error ? <ErrorMessage message={error} /> : null}

      {!loading && items.length === 0 ? (
        <EmptyState title={t('guidelines.emptyTitle')} message={t('guidelines.emptyMessage')} />
      ) : null}

      {!loading && items.length > 0 ? (
        <ul className="guidelines-list reveal is-in reveal-delay-2">
          {items.map((item) => (
            <li key={item.slug}>
              <Link to={`/guidelines/${item.slug}`} className="guideline-card glass-panel-soft">
                <span className="guideline-card__type">
                  {item.type ?? t('guidelines.typeGuide')}
                </span>
                <h3 className="guideline-card__title">{item.title}</h3>
                {item.summary ? <p className="guideline-card__summary">{item.summary}</p> : null}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

export function GuidelinesPage() {
  const { t } = useI18n();

  return (
    <section className="guidelines-page site-pad-x">
      <div className="guidelines-layout">
        <aside className="guidelines-toc glass-panel-soft" aria-label={t('guidelines.navAria')}>
          <NavLink
            to="/guidelines"
            end
            className={({ isActive }) =>
              `guidelines-toc__link${isActive ? ' active' : ''}`
            }
          >
            {t('guidelines.index')}
          </NavLink>
        </aside>
        <div className="guidelines-content">
          <Routes>
            <Route index element={<GuidelinesIndex />} />
            <Route path=":slug" element={<GuidelineArticle />} />
          </Routes>
        </div>
      </div>
    </section>
  );
}
